<script setup lang="ts">
import { computed } from 'vue'
import { RrButton, RrDrawer, RrForm, RrInput, RrSelect, RrTag } from '@xjlv2/ui'
import type { MaterialType, SaveSaasMaterialRequest } from '../../types'

const props = defineProps<{
  open: boolean
  editingId: number | null
  form: SaveSaasMaterialRequest
  tagText: string
  scopeOptions: Array<{ code: string; name: string }>
  typeOptions: Array<{ value: MaterialType; label: string }>
  loading: boolean
  canCreate: boolean
  canUpdate: boolean
}>()

const emit = defineEmits<{
  'update:open': [val: boolean]
  'update:tagText': [val: string]
  save: []
}>()

const moduleScopeOptions = computed(() =>
  props.scopeOptions.map((o) => ({ label: o.name, value: o.code }))
)

const statusOptions = [
  { label: '草稿', value: 'DRAFT' },
  { label: '启用', value: 'ACTIVE' },
  { label: '已归档', value: 'ARCHIVED' }
]

const isReadonly = computed(() => {
  return props.loading || (props.editingId == null ? !props.canCreate : !props.canUpdate)
})

const tagsPreview = computed(() => {
  return props.tagText.split(',').map((t) => t.trim()).filter(Boolean)
})
</script>

<template>
  <RrDrawer
    :open="open"
    :title="editingId == null ? '登记外链 / 新增文稿' : '编辑素材'"
    :width="760"
    :footer="null"
    @close="emit('update:open', false)"
  >
    <RrForm @submit.prevent="emit('save')">
      <div class="editor-drawer-body">
        <fieldset :disabled="isReadonly" class="editor-form-grid">
          <label>
            <span class="label-text">业务模块 <em class="required-star">*</em></span>
            <RrSelect
              v-model:value="form.moduleCode"
              :options="moduleScopeOptions"
              :disabled="editingId != null || isReadonly"
              placeholder="请选择业务模块"
            />
          </label>
          <label>
            <span class="label-text">素材类型 <em class="required-star">*</em></span>
            <RrSelect
              v-model:value="form.type"
              :options="typeOptions"
              :disabled="isReadonly"
              placeholder="请选择素材类型"
            />
          </label>
          <label>
            <span class="label-text">素材名称 <em class="required-star">*</em></span>
            <RrInput
              v-model:value="form.name"
              :disabled="isReadonly"
              placeholder="例如：首页主视觉"
              :maxlength="200"
              required
            />
          </label>
          <label>
            <span class="label-text">展示标题</span>
            <RrInput
              v-model:value="form.title"
              :disabled="isReadonly"
              placeholder="可选"
              :maxlength="200"
            />
          </label>
          <label>
            <span class="label-text">初始状态</span>
            <RrSelect
              v-model:value="form.status"
              :options="statusOptions"
              :disabled="isReadonly"
            />
          </label>
          <label>
            <span class="label-text">产品编码</span>
            <RrInput
              v-model:value="form.productCode"
              :disabled="isReadonly"
              placeholder="可选"
              :maxlength="64"
            />
          </label>
          <label>
            <span class="label-text">应用编码</span>
            <RrInput
              v-model:value="form.appCode"
              :disabled="isReadonly"
              placeholder="可选"
              :maxlength="64"
            />
          </label>
          <label>
            <span class="label-text">文件名</span>
            <RrInput
              v-model:value="form.fileName"
              :disabled="isReadonly"
              placeholder="hero.jpg"
              :maxlength="200"
            />
          </label>
          <label>
            <span class="label-text">MIME 类型</span>
            <RrInput
              v-model:value="form.mimeType"
              :disabled="isReadonly"
              placeholder="image/jpeg"
              :maxlength="128"
            />
          </label>
          <label class="form-span">
            <span class="label-text">
              文件 URL
              <em v-if="form.type !== 'ARTICLE'" class="required-star">*</em>
            </span>
            <RrInput
              v-model:value="form.fileUrl"
              :disabled="isReadonly"
              :required="form.type !== 'ARTICLE'"
              placeholder="https://...；文稿可留空"
            />
          </label>
          <label class="form-span">
            <span class="label-text">对象存储 Key</span>
            <RrInput
              v-model:value="form.objectKey"
              :disabled="isReadonly"
              placeholder="例如 tenant/module/file.jpg"
            />
          </label>
          <label class="form-span">
            <span class="label-text">封面 URL</span>
            <RrInput
              v-model:value="form.coverUrl"
              :disabled="isReadonly"
              placeholder="音视频和文稿可配置封面"
            />
          </label>
          <label>
            <span class="label-text">文件大小（字节）</span>
            <RrInput
              :value="form.fileSize"
              type="number"
              min="0"
              :disabled="isReadonly"
              @update:value="form.fileSize = $event === '' ? undefined : Number($event)"
            />
          </label>
          <label>
            <span class="label-text">时长（秒）</span>
            <RrInput
              :value="form.durationSeconds"
              type="number"
              min="0"
              :disabled="isReadonly"
              @update:value="form.durationSeconds = $event === '' ? undefined : Number($event)"
            />
          </label>
          <label>
            <span class="label-text">宽度（px）</span>
            <RrInput
              :value="form.width"
              type="number"
              min="0"
              :disabled="isReadonly"
              @update:value="form.width = $event === '' ? undefined : Number($event)"
            />
          </label>
          <label>
            <span class="label-text">高度（px）</span>
            <RrInput
              :value="form.height"
              type="number"
              min="0"
              :disabled="isReadonly"
              @update:value="form.height = $event === '' ? undefined : Number($event)"
            />
          </label>
          <label>
            <span class="label-text">分类</span>
            <RrInput
              v-model:value="form.category"
              :disabled="isReadonly"
              placeholder="例如：首页运营"
              :maxlength="100"
            />
          </label>
          <label>
            <span class="label-text">来源</span>
            <RrInput
              v-model:value="form.source"
              :disabled="isReadonly"
              placeholder="saas / import"
              :maxlength="64"
            />
          </label>
          <label class="form-span">
            <span class="label-text">标签</span>
            <RrInput
              :value="tagText"
              :disabled="isReadonly"
              placeholder="多个标签用英文逗号分隔，如: 运营, 首页, 轮播"
              @update:value="emit('update:tagText', $event)"
            />
            <div v-if="tagsPreview.length" class="tags-preview-list">
              <RrTag v-for="tag in tagsPreview" :key="tag">{{ tag }}</RrTag>
            </div>
          </label>
          <label class="form-span">
            <span class="label-text">版权信息</span>
            <RrInput
              v-model:value="form.copyright"
              :disabled="isReadonly"
              placeholder="版权归属或授权说明"
            />
          </label>
          <label v-if="form.type === 'ARTICLE'" class="form-span">
            <span class="label-text">文稿正文</span>
            <RrInput
              v-model:value="form.content"
              type="textarea"
              :rows="10"
              :disabled="isReadonly"
              placeholder="输入文稿正文"
            />
          </label>
          <label class="form-span">
            <span class="label-text">素材描述</span>
            <RrInput
              v-model:value="form.description"
              type="textarea"
              :rows="4"
              :disabled="isReadonly"
              placeholder="素材业务说明或备忘"
            />
          </label>
        </fieldset>

        <div class="drawer-action-bar">
          <RrButton @click="emit('update:open', false)">取消</RrButton>
          <RrButton
            type="primary"
            html-type="submit"
            :disabled="isReadonly || !form.name || (form.type !== 'ARTICLE' && !form.fileUrl)"
            :loading="loading"
          >
            {{ editingId == null ? '登记素材' : '保存修改' }}
          </RrButton>
        </div>
      </div>
    </RrForm>
  </RrDrawer>
</template>

<style scoped>
.editor-drawer-body {
  padding: 20px;
  overflow-y: auto;
}

.editor-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin: 0;
  padding: 0;
  border: 0;
}

.editor-form-grid label {
  display: grid;
  gap: 6px;
  color: var(--rr-color-text);
  font-size: 13px;
  font-weight: 500;
}

.label-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.required-star {
  color: var(--rr-color-danger);
  font-style: normal;
}

.editor-form-grid .form-span {
  grid-column: 1 / -1;
}

.tags-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.drawer-action-bar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--rr-color-border);
}

@media (max-width: 700px) {
  .editor-form-grid {
    grid-template-columns: 1fr;
  }
  .editor-form-grid .form-span {
    grid-column: auto;
  }
}
</style>
